import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircleOutline as CompletarIcon,
  Close as CloseIcon,
  RadioButtonUnchecked as PendienteIcon,
} from '@mui/icons-material';
import type { CrmActividad, CrmActividadCreate } from '../../../types/crm';

interface CrmTimelineProps {
  actividades: CrmActividad[];
  loading?: boolean;
  onCreate: (dto: Omit<CrmActividadCreate, 'contactoId' | 'oportunidadId'>) => Promise<void>;
  onToggleEstado?: (actividad: CrmActividad) => Promise<void>;
  canEdit?: boolean;
  /** Si true, omite el título y el botón (útil dentro de un Accordion). */
  hideHeader?: boolean;
  /** Control externo del formulario (p. ej. botón en AccordionSummary). */
  formOpen?: boolean;
  onFormOpenChange?: (open: boolean) => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-AR');
};

const CrmTimeline: React.FC<CrmTimelineProps> = ({
  actividades,
  loading = false,
  onCreate,
  onToggleEstado,
  canEdit = true,
  hideHeader = false,
  formOpen,
  onFormOpenChange,
}) => {
  const [mostrarFormInterno, setMostrarFormInterno] = useState(false);
  const [tipo, setTipo] = useState('Nota');
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const controlled = formOpen !== undefined;
  const mostrarForm = controlled ? formOpen : mostrarFormInterno;

  const setMostrarForm = (open: boolean) => {
    if (controlled) onFormOpenChange?.(open);
    else setMostrarFormInterno(open);
  };

  const resetForm = () => {
    setTitulo('');
    setCuerpo('');
    setFechaVencimiento('');
    setTipo('Nota');
  };

  const handleCancel = () => {
    resetForm();
    setMostrarForm(false);
  };

  const handleSubmit = async () => {
    if (!titulo.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        tipo,
        titulo: titulo.trim(),
        cuerpo: cuerpo.trim() || null,
        fechaVencimiento: fechaVencimiento || null,
      });
      resetForm();
      setMostrarForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (a: CrmActividad) => {
    if (!onToggleEstado) return;
    setTogglingId(a.id);
    try {
      await onToggleEstado(a);
    } finally {
      setTogglingId(null);
    }
  };

  const showVencimientoField = tipo === 'Tarea' || tipo === 'Llamada' || tipo === 'Reunion' || tipo === 'Email';

  return (
    <Stack spacing={1.5}>
      {!hideHeader && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Actividades
          </Typography>
          {canEdit && !mostrarForm && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setMostrarForm(true)}
              sx={{ textTransform: 'none' }}
            >
              Nueva actividad
            </Button>
          )}
        </Stack>
      )}

      {canEdit && mostrarForm && (
        <Stack spacing={1.5} sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              label="Tipo"
              size="small"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="Nota">Nota</MenuItem>
              <MenuItem value="Tarea">Tarea</MenuItem>
              <MenuItem value="Llamada">Llamada</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="Reunion">Reunión</MenuItem>
            </TextField>
            <TextField
              label="Título"
              size="small"
              fullWidth
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              autoFocus
            />
            {showVencimientoField && (
              <TextField
                label="Vencimiento"
                type="datetime-local"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
                sx={{ minWidth: 210 }}
              />
            )}
          </Stack>
          <TextField
            label="Detalle"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={cuerpo}
            onChange={(e) => setCuerpo(e.target.value)}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleSubmit}
              disabled={saving || !titulo.trim()}
              sx={{ textTransform: 'none' }}
            >
              Agregar actividad
            </Button>
            <Button
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
          </Stack>
        </Stack>
      )}

      {(mostrarForm || !hideHeader) && <Divider />}

      {loading && (
        <Typography variant="body2" color="text.secondary">
          Cargando actividades...
        </Typography>
      )}

      {!loading && actividades.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No hay actividades registradas.
        </Typography>
      )}

      <Stack spacing={1.5}>
        {actividades.map((a) => (
          <Box
            key={a.id}
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: a.completada ? 'success.light' : 'divider',
              borderRadius: 2,
              bgcolor: '#fff',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={a.tipo} color="primary" variant="outlined" />
              <Chip
                size="small"
                label={a.completada ? 'Completada' : 'Pendiente'}
                color={a.completada ? 'success' : 'warning'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {formatDateTime(a.fechaCreacion)} · {a.usuario}
              </Typography>
              {canEdit && onToggleEstado && (
                <Tooltip title={a.completada ? 'Marcar pendiente' : 'Marcar completada'}>
                  <span>
                    <IconButton
                      size="small"
                      color={a.completada ? 'warning' : 'success'}
                      disabled={togglingId === a.id}
                      onClick={() => void handleToggle(a)}
                    >
                      {a.completada ? <PendienteIcon fontSize="small" /> : <CompletarIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 600 }}>
              {a.titulo}
            </Typography>
            {a.cuerpo && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {a.cuerpo}
              </Typography>
            )}
            {a.fechaVencimiento && (
              <Typography variant="caption" color="text.secondary">
                Vence: {formatDateTime(a.fechaVencimiento)}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

export default CrmTimeline;
